import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import apiClient from "../utils/apiClient";
import { cleanName, fetchVideoAnalysis, getSelectedSport } from "../utils/helpers";
import VideoAnalyticsModal from "./VideoAnalyticsModal";
import { Video, BookOpen, PlayCircle, ChevronLeft, Users, RotateCcw, Zap, Target, Volleyball, ChartSpline, Handshake, ShieldHalf, TrendingUp, Shield, HelpCircle } from "lucide-react";
import { SimpleTooltip, Tabs, TabsList, TabsTrigger, TabsContent } from "./design-system";
import GameStatsTab from "./GameStatsTabs";
// Icons consolidated to lucide-react (BarChart3, Image)
import BasketballRadarChart from "./BasketballRadarChart";

import VideoAnalyticsTable from './VideoAnalyticsTable';
import PlayerProfileMetric from "../PlayerProfileMetric";
import OffensiveTab from "./OffensiveTab";
import DefensiveTab from "./DefensiveTab";
import SpecialTeamTab from "../SpecialTeamTab";
import AthleticDashboard from "./AthleticDashboard";
import { parsePlayerData } from "../utils/constant";
import MbbRadarChart from "./MbbRadarChart";
import StatisticsTable from "./StatisticsTable";
import UploadTrainingStats from "./player_profiles/UploadTrainingStats";
import PracticeStatisticsTable from "./PracticeStatisticsTable";
import PerGameStatsTable from "./PerGameStatsTable";

// Tab styles for Radix Tabs - using Tailwind classes inline instead of custom CSS


const TeamPlayerProfileNew = () => {
    let { playerId } = useParams();
    playerId = cleanName(playerId);
    const navigate = useNavigate();
    const [player, setPlayer] = useState(null);
    const [playerStats, setPlayerStats] = useState(null);
    const [playerPracticeStats, setPlayerPracticeStats] = useState(null);
    const [playerPractStatsError, setPlayerPracticeStatsError]= useState(null)
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sportType, setSportType] = useState("");
    const [activeMainTab, setActiveMainTab] = useState("playerStats");
    const [seasonStats, setSeasonStats] = useState([]);
    const { state } = useLocation();
    // Removed custom tooltip states as we're using SimpleTooltip from design-system
    const [uploadTraining, setUploadTraining] = useState(false);

    const playerData = state?.playerData;

    useEffect(() => {
        if (playerId) {
            fetchNewStats()
            const currentSport = getSelectedSport();
            setSportType(currentSport);
            fetchData(currentSport);
        }
    }, [playerId]);

    const fetchNewStats = async () => {
        try {
            setLoading(true);

            const sport = getSelectedSport();
            const baseUrl = `https://slam-all-python-359065791766.us-central1.run.app/${sport}/players-stats-mia/search-by-player?schema=${sport}`;

            const response = await apiClient.post(baseUrl, {
                player_name: playerId
            });

            const statsData = response?.data?.data?.[0]; // Assuming API returns array

            if (statsData) {
                const normalize = parsePlayerData(statsData)
                setPlayer(normalize); // Update UI or state
            } else {
                console.warn("No stats found for the player?.");
            }
        } catch (error) {
            console.error("Error fetching stats:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchData = async (sport) => {
        setError(null);
        setLoading(true);
        if (sport === "MBB") {
            fetchNewStats()
        }
        else {

            try {
                const [playerResponse, statsResponse] = await Promise.all([
                    apiClient.get(`${sport}/players/${playerId}`),
                    apiClient.get(`${sport}/players/${playerId}/stats`),
                ]);

                // Store raw stats data
                setPlayerStats(statsResponse?.data?.data);

                const seasonStatsData = statsResponse?.data?.season_stats || [];
                setSeasonStats(seasonStatsData);

                let playerData = playerResponse.data;

                if (
                    !playerData.name &&
                    !playerData.first_name &&
                    playerResponse.data.data
                ) {
                    playerData = playerResponse.data.data;
                } else if (
                    !playerData.name &&
                    !playerData.first_name &&
                    playerResponse.data.results
                ) {
                    playerData = playerResponse.data.results;
                }

                if (sport === "CBB") {
                    setPlayer(processBaseballPlayer(playerData, statsResponse?.data?.data));
                } else {
                    // setPlayer(processGenericPlayer(playerData, statsResponse?.data?.data));
                }
            } catch (error) {
                console.error(`Error fetching player details for ${playerId}:`, error);
                setError(`Failed to load player details. ${error.message}`);
            } finally {
                setLoading(false);
            }
        }
    };

    // fetch player training data:
    useEffect(() => {
        const fetchPlayerTrainingData = async () => {
            try {
                const response = await fetch(
                    "https://slam-all-python-359065791766.us-central1.run.app/MBB/training-stats/get-stats?schema=MBB",
                    {
                        method: "POST",
                        headers: {
                            accept: "application/json",
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                            player_name: player.name,
                        }),
                    }
                );

                
                if (!response.ok) {
                  const res = await response.json()
                    console.log(res);
                    
                    setPlayerPracticeStatsError(res?.detail)
                    throw new Error(`HTTP error! status: ${response.status}`);                   
                }

                const data = await response.json();


                setPlayerPracticeStats(data);
                // Here you would typically set the data to state, e.g.:
                // setPlayerData(data);
            } catch (error) {
                console.error("Error fetching player training data:", error);
                
                // Handle error state here
            }
        };

        fetchPlayerTrainingData();
    }, [player]);

    const tooltipDefinitions = {
        'Offensive BPR': `A player’s predicted per-possession offensive value to his team, against an average opponent. Offensive BPR reflects how much impact a player has on his team’s scoring and overall offensive efficiency when he is on the court.`,
        'Defensive BPR': 'A player’s predicted per-possession defensive value to his team, against an average opponent. Defensive BPR measures how much defensive impact a player provides, including contributions like stopping opponent scoring, rebounding, steals, and blocks.',
        'Overall BPR': 'A player’s total predicted per-possession value to his team, combining both Offensive BPR and Defensive BPR. Overall BPR summarizes a player’s complete impact on the game when he is on the court.'
    };
    // Removed custom tooltip show handler

    // Removed custom tooltip hide handler

    const CareerStatsComponent = ({ player, seasonStats, sportType }) => {
        const [statisticsTab, setStatisticsTab] = useState("PerGameStatsTable");

        if (sportType === "MBB" || sportType === "WBB") {
            return (
                <div className="space-y-8">
                    {/* BPR Scores Row */}
                    <h3 style={{ marginBottom: '-18px' }} className="text-xl font-bold text-[#F5F5F5] mb-2">
                        Bayesian Performance Rating
                    </h3>
                    {/* Removed custom tooltip */}
                    <div className="grid grid-cols-1 gap-6 mt-0 md:grid-cols-3">
                        {/* Offensive BPR */}
                        <div className="bg-[#0A0A0A] rounded-[14px] shadow-sm border border-[#1A1A1A] p-6">
                            <div className="">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="text-sm font-medium text-[#999999]">
                                        Offensive BPR
                                    </div>
                                    {tooltipDefinitions['Offensive BPR'] && (
                                        <SimpleTooltip title={tooltipDefinitions['Offensive BPR']}>
                                            <HelpCircle
                                                size={13}
                                                style={{
                                                    color: "#94a3b8",
                                                    opacity: 0.7,
                                                    cursor: 'help'
                                                }}
                                            />
                                        </SimpleTooltip>
                                    )}
                                </div>
                                <div className="text-3xl font-bold text-[#F5F5F5] mb-2">
                                    {player?.obprPredicted ? player?.obprPredicted.toString() : 'N/A'}
                                </div>
                                <div className="text-sm font-medium text-blue-500">
                                    Above D1 Average
                                </div>
                                <div className="w-full bg-[#1A1A1A] rounded-full h-2 mt-3">
                                    <div className="h-2 bg-[#E8B800] rounded-full" style={{ width: '70%' }}></div>
                                </div>
                            </div>
                        </div>

                        {/* Defensive BPR */}
                        <div className="bg-[#0A0A0A] rounded-[14px] shadow-sm border border-[#1A1A1A] p-6">
                            <div className="">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="text-sm font-medium text-[#999999]">
                                        Defensive BPR
                                    </div>
                                    {tooltipDefinitions['Defensive BPR'] && (
                                        <SimpleTooltip title={tooltipDefinitions['Defensive BPR']}>
                                            <HelpCircle
                                                size={13}
                                                style={{
                                                    color: "#94a3b8",
                                                    opacity: 0.7,
                                                    cursor: 'help'
                                                }}
                                            />
                                        </SimpleTooltip>
                                    )}
                                </div>
                                <div className="text-3xl font-bold text-[#F5F5F5] mb-2">
                                    {player?.dbprPredicted ? player?.dbprPredicted.toString() : "N/A"}
                                </div>
                                <div className="text-sm font-medium text-green-500">
                                    Above D1 Average
                                </div>
                                <div className="w-full bg-[#1A1A1A] rounded-full h-2 mt-3">
                                    <div className="h-2 bg-[#E8B800] rounded-full" style={{ width: '55%' }}></div>
                                </div>
                            </div>
                        </div>

                        {/* Overall BPR */}
                        <div className="bg-[#0A0A0A] rounded-[14px] shadow-sm border border-[#1A1A1A] p-6">
                            <div className="">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="text-sm font-medium text-[#999999]">
                                        Overall BPR
                                    </div>
                                    {tooltipDefinitions['Overall BPR'] && (
                                        <SimpleTooltip title={tooltipDefinitions['Overall BPR']}>
                                            <HelpCircle
                                                size={13}
                                                style={{
                                                    color: "#94a3b8",
                                                    opacity: 0.7,
                                                    cursor: 'help'
                                                }}
                                            />
                                        </SimpleTooltip>
                                    )}
                                </div>
                                <div className="text-3xl font-bold text-[#F5F5F5] mb-2">
                                    {player?.bprPredicted ? player?.bprPredicted.toString() : "N/A"}
                                </div>
                                <div className="text-sm font-medium text-blue-500">
                                    Above D1 Average
                                </div>
                                <div className="w-full bg-[#1A1A1A] rounded-full h-2 mt-3">
                                    <div className="h-2 bg-[#E8B800] rounded-full" style={{ width: '65%' }}></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Performance Radar and Career Totals Side by Side */}
                    <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                        {/* Performance Radar */}
                        <div className="bg-[#0A0A0A] rounded-[14px] shadow-sm border border-[#1A1A1A] p-6 h-full">
                            <h3 className="text-xl font-bold text-[#F5F5F5] mb-6">
                                Performance Radar
                            </h3>
                            <div className="flex justify-center">
                                <div className="w-80 h-80">
                                    <MbbRadarChart playerData={player} />
                                </div>
                            </div>
                        </div>

                        {/* Career Totals - Updated Design */}
                        <div className="bg-[#0A0A0A] rounded-[14px] shadow-sm border border-[#1A1A1A] p-6 h-full flex flex-col">
                            <h3 className="text-xl font-bold text-[#F5F5F5] mb-6">
                                Career Totals
                            </h3>
                            <div className="grid items-center flex-1 grid-cols-2 gap-8">
                                {/* Points */}
                                <div className="text-center">
                                    <div className="flex justify-center mb-3">
                                        <div className="text-[#E8B800] transition-all duration-300 hover:scale-110 hover:text-[#E8B800]">
                                            <Target size={32} />
                                        </div>
                                    </div>
                                    <div className="text-3xl font-bold text-[#F5F5F5] mb-1">
                                        {player?.scoring + "%" || 'N/A'}

                                    </div>
                                    <div className="text-sm font-medium text-[#999999]">
                                        Points
                                    </div>
                                </div>

                                {/* Rebounds */}
                                <div className="text-center">
                                    <div className="flex justify-center mb-3">
                                        <div className="text-[#E8B800] transition-all duration-300 hover:scale-110 hover:text-[#E8B800]">
                                            <TrendingUp size={32} />
                                        </div>
                                    </div>
                                    <div className="text-3xl font-bold text-[#F5F5F5] mb-1">
                                        {player?.rebPct + "%" || 'N/A'}
                                    </div>
                                    <div className="text-sm font-medium text-[#999999]">
                                        Rebounds
                                    </div>
                                </div>

                                {/* Assists */}
                                <div className="text-center">
                                    <div className="flex justify-center mb-3">
                                        <div className="text-[#E8B800] transition-all duration-300 hover:scale-110 hover:text-[#E8B800]">
                                            <Users size={32} />
                                        </div>
                                    </div>
                                    <div className="text-3xl font-bold text-[#F5F5F5] mb-1">
                                        {player?.assistRate + "%" || 'N/A'}

                                    </div>
                                    <div className="text-sm font-medium text-[#999999]">
                                        Assists
                                    </div>
                                </div>

                                {/* Steals */}
                                <div className="text-center">
                                    <div className="flex justify-center mb-3">
                                        <div className="text-[#E8B800] transition-all duration-300 hover:scale-110 hover:text-[#E8B800]">
                                            <Shield size={32} />
                                        </div>
                                    </div>
                                    <div className="text-3xl font-bold text-[#F5F5F5] mb-1">
                                        {player?.stealRate + "%" || 'N/A'}
                                    </div>
                                    <div className="text-sm font-medium text-[#999999]">
                                        Steals
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Career Statistics Table */}
                    {/* Career Statistics Table */}
                    <div
                        className="bg-[#0A0A0A] rounded-[14px] shadow-sm border border-[#1A1A1A] p-6"
                    >
                        <div className="flex items-center space-x-8 border-b border-[#1A1A1A]">
                            <button
                                onClick={() => {
                                    setStatisticsTab("PerGameStatsTable");
                                }}
                                className={`relative px-1 py-3 text-sm font-medium transition-colors duration-200 ${statisticsTab === "PerGameStatsTable"
                                    ? "text-[#E8B800]"
                                    : "text-[#999999] hover:text-[#F5F5F5]"
                                    }`}
                            >
                                Core Stats
                                {statisticsTab === "PerGameStatsTable" && (
                                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#E8B800] rounded-full"></div>
                                )}
                            </button>
                            <button
                                onClick={() => {
                                    setStatisticsTab("advancestats");
                                }}
                                className={`relative px-1 py-3 text-sm font-medium transition-colors duration-200 ${statisticsTab === "advancestats"
                                    ? "text-[#E8B800]"
                                    : "text-[#999999] hover:text-[#F5F5F5]"
                                    }`}
                            >
                                Advance Stats
                                {statisticsTab === "advancestats" && (
                                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#E8B800] rounded-full"></div>
                                )}
                            </button>

                            <button
                                onClick={() => {
                                    setStatisticsTab("practicestats");
                                }}
                                className={`relative px-1 py-3 text-sm font-medium transition-colors duration-200 ${statisticsTab === "practicestats"
                                    ? "text-[#E8B800]"
                                    : "text-[#999999] hover:text-[#F5F5F5]"
                                    }`}
                            >
                                Practice Stats
                                {statisticsTab === "practicestats" && (
                                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#E8B800] rounded-full"></div>
                                )}
                            </button>


                        </div>
                        {statisticsTab === "advancestats" ? (
                            <StatisticsTable isDarkMode={true} player={player} />
                        ) : statisticsTab === "PerGameStatsTable" ? <PerGameStatsTable
                            isDarkMode={true}
                            stats={player?.core_stats}
                        /> : (
                            <PracticeStatisticsTable
                                isDarkMode={true}
                                player={playerPracticeStats}
                                error={playerPractStatsError}
                            />
                        )}
                    </div>
                </div>
            );
        }
    };
    // Process baseball player data
    const processBaseballPlayer = (playerData, statsData) => {
        const aggregatedStats = statsData.reduce(
            (acc, game) => {
                return {
                    totalGames: acc.totalGames + 1,
                    totalAB: acc.totalAB + (game.AB || 0),
                    totalR: acc.totalR + (game.R || 0),
                    totalH: acc.totalH + (game.H || 0),
                    totalRBI: acc.totalRBI + (game.RBI || 0),
                    totalHR: acc.totalHR + (game.HR || 0),
                    totalBB: acc.totalBB + (game.BB || 0),
                    totalSO: acc.totalSO + (game.SO || 0),
                    totalIP: acc.totalIP + (game.IP || 0),
                    totalBF: acc.totalBF + (game.BF || 0),
                    totalER: acc.totalER + (game.ER || 0),
                    totalPIT: acc.totalPIT + (game.PIT || 0),
                    totalW: acc.totalW + (game.W || 0),
                    totalL: acc.totalL + (game.L || 0),
                    totalSV: acc.totalSV + (game.SV || 0),
                };
            },
            {
                totalGames: 0,
                totalAB: 0,
                totalR: 0,
                totalH: 0,
                totalRBI: 0,
                totalHR: 0,
                totalBB: 0,
                totalSO: 0,
                totalIP: 0,
                totalBF: 0,
                totalER: 0,
                totalPIT: 0,
                totalW: 0,
                totalL: 0,
                totalSV: 0,
            }
        );

        const battingAverage = aggregatedStats.totalAB > 0
            ? (aggregatedStats.totalH / aggregatedStats.totalAB).toFixed(3)
            : ".000";

        const onBasePercentage = aggregatedStats.totalAB + aggregatedStats.totalBB > 0
            ? ((aggregatedStats.totalH + aggregatedStats.totalBB) / (aggregatedStats.totalAB + aggregatedStats.totalBB)).toFixed(3)
            : ".000";

        const sluggingPercentage = aggregatedStats.totalAB > 0
            ? ((aggregatedStats.totalH + aggregatedStats.totalHR) / aggregatedStats.totalAB).toFixed(3)
            : ".000";

        const ops = (parseFloat(onBasePercentage) + parseFloat(sluggingPercentage)).toFixed(3);

        const era = aggregatedStats.totalIP > 0
            ? ((aggregatedStats.totalER * 9) / aggregatedStats.totalIP).toFixed(2)
            : "0.00";

        const isPitcher = statsData.some((game) => game.IP > 0) ||
            (playerData.position && playerData.position.toLowerCase().includes("p"));

        const specialAttrs = [];
        if (isPitcher) {
            if (parseFloat(era) < 3.0) {
                specialAttrs.push("TOP PITCHER");
            } else if (aggregatedStats.totalSO > 15) {
                specialAttrs.push("STRIKEOUT ARTIST");
            } else {
                specialAttrs.push("PITCHER");
            }
        } else {
            if (parseFloat(battingAverage) > 0.3) {
                specialAttrs.push("CONTACT HITTER");
            } else if (aggregatedStats.totalHR > 5) {
                specialAttrs.push("POWER HITTER");
            } else if (aggregatedStats.totalRBI > 20) {
                specialAttrs.push("RUN PRODUCER");
            } else {
                specialAttrs.push("VERSATILE");
            }
        }

        let team = playerData.team || playerData.team_name || "Unknown Team";
        let teamAbbrev = "";

        if (statsData.length > 0 && statsData[0].Team) {
            team = statsData[0].Team;
            teamAbbrev = statsData[0].TeamAbbrev || "";
        }

        return {
            id: playerData.id || playerData.player_id || playerId,
            position: playerData.position || playerData.Position || "Unknown",
            fullName: playerData.name || `${playerData.first_name || ""} ${playerData.last_name || ""}`.trim(),
            firstName: playerData.first_name || playerData.name?.split(" ")[0] || "",
            lastName: playerData.last_name || playerData.name?.split(" ").slice(1).join(" ") || "",
            year: playerData.year || playerData.class_year || "Unknown",
            height: playerData.height || playerData?.Height || "",
            Player: playerData?.Player || playerData?.name,
            weight: playerData.weight || playerData?.Weight || "",
            team: team,
            teamAbbrev: teamAbbrev,
            battingAverage: battingAverage,
            homeRuns: aggregatedStats.totalHR,
            ops: ops,
            exitVelo: playerData.exit_velocity || Math.floor(Math.random() * 15 + 85),
            swingMiss: playerData.swing_miss_rate || Math.floor(Math.random() * 30),
            speed: playerData.speed || (Math.random() * 5 + 25).toFixed(1),
            imageUrl: playerData.image_url || playerData.photo || "/SLAM_logo.png",
            specialAttrs: specialAttrs,
            obp: onBasePercentage,
            slg: sluggingPercentage,
            games: aggregatedStats.totalGames,
            atBats: aggregatedStats.totalAB,
            runs: aggregatedStats.totalR,
            hits: aggregatedStats.totalH,
            rbi: aggregatedStats.totalRBI,
            era: era,
            ip: aggregatedStats.totalIP,
            bf: aggregatedStats.totalBF,
            er: aggregatedStats.totalER,
            bb: aggregatedStats.totalBB,
            so: aggregatedStats.totalSO,
            pit: aggregatedStats.totalPIT,
            wins: aggregatedStats.totalW,
            losses: aggregatedStats.totalL,
            saves: aggregatedStats.totalSV,
            bio: playerData.bio || playerData.biography || "No biography available.",
            highlights: playerData.highlights || [],
            isPitcher: isPitcher,
        };
    };

    const processGenericPlayer = (playerData, statsData) => {
        return {
            id: playerData.id || playerData.player_id || playerId,
            position: playerData.Position || playerData.position || "Unknown",
            fullName: playerData.name || `${playerData.first_name || ""} ${playerData.last_name || ""}`.trim() || playerData?.player,
            firstName: playerData.first_name || playerData.name?.split(" ")[0] || "",
            lastName: playerData.last_name || playerData.name?.split(" ").slice(1).join(" ") || "",
            year: playerData.year || playerData.class_year || "Unknown",
            height: playerData.Height || playerData.height || "",
            weight: playerData.Weight || playerData.weight || "",
            team: playerData.team || playerData.team_name || "Unknown Team",
            teamAbbrev: "",
            imageUrl: playerData.image_url || playerData.photo || "/SLAM_logo.png",
            specialAttrs: ["ATHLETE"],
            bio: playerData.bio || playerData.biography || "No biography available.",
            highlights: playerData.highlights || [],
        };
    };

    const getPositionColors = (position, sport) => {
        if (!position) return "from-blue-600 to-indigo-700";

        position = position.toLowerCase();

        if (sport === "CBB") {
            if (position.includes("p") || position.includes("rh") || position.includes("lh")) {
                return "from-blue-600 to-indigo-700";
            } else if (position.includes("c")) {
                return "from-amber-500 to-orange-600";
            } else if (position.includes("ss") || position.includes("2b") || position.includes("3b") || position.includes("1b")) {
                return "from-red-500 to-pink-600";
            } else if (position.includes("of") || position.includes("lf") || position.includes("cf") || position.includes("rf")) {
                return "from-green-600 to-teal-500";
            } else {
                return "from-purple-600 to-indigo-600";
            }
        } else if (sport === "WBB" || sport === "MBB") {
            if (position.includes("pg")) {
                return "from-[#E8B800] to-amber-600";
            } else if (position.includes("sg")) {
                return "from-orange-500 to-red-600";
            } else if (position.includes("sf")) {
                return "from-green-500 to-emerald-600";
            } else if (position.includes("pf")) {
                return "from-blue-500 to-indigo-600";
            } else if (position.includes("c")) {
                return "from-purple-600 to-violet-700";
            } else {
                return "from-[#111111] to-[#0A0A0A]";
            }
        } else {
            return "from-blue-600 to-indigo-700";
        }
    };

    const getPositionBadgeColor = (position, sport) => {
        if (!position) return "bg-indigo-600";

        position = position.toLowerCase();

        if (sport === "CBB") {
            if (position.includes("p") || position.includes("rh") || position.includes("lh")) {
                return "bg-indigo-600";
            } else if (position.includes("c")) {
                return "bg-amber-600";
            } else if (position.includes("ss") || position.includes("2b") || position.includes("3b") || position.includes("1b")) {
                return "bg-red-600";
            } else if (position.includes("of") || position.includes("lf") || position.includes("cf") || position.includes("rf")) {
                return "themePrimary";
            } else {
                return "bg-purple-600";
            }
        } else if (sport === "WBB" || sport === "MBB") {
            if (position.includes("pg")) {
                return "bg-amber-600";
            } else if (position.includes("sg")) {
                return "bg-red-600";
            } else if (position.includes("sf")) {
                return "bg-emerald-600";
            } else if (position.includes("pf")) {
                return "bg-indigo-600";
            } else if (position.includes("c")) {
                return "bg-violet-700";
            } else {
                return "bg-[#111111]";
            }
        } else {
            return "bg-indigo-600";
        }
    };

    const getSportBadge = (sport) => {
        if (sport === "CBB") {
            return { color: "themeSecondary", label: "BASEBALL" };
        } else if (sport === "WBB" || sport === "MBB") {
            return { color: "bg-orange-600", label: "BASKETBALL" };
        } else {
            return { color: "bg-purple-600", label: "UNKNOWN" };
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center p-8 bg-[#0a0a0a] transition-colors">
                <div className="w-12 h-12 border-t-2 border-b-2 border-[#E8B800] rounded-full animate-spin"></div>
                <p className="ml-3 text-[#999999]">
                    Loading player details...
                </p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-900 border border-red-700 text-red-300 px-4 py-3 rounded-[14px] relative mb-6">
                <strong className="font-bold">Error: </strong>
                <span className="block sm:inline">{error}</span>
                <button onClick={() => navigate(-1)} className="mt-3 bg-[#111111] text-red-300 hover:bg-red-800 px-4 py-2 rounded-[14px] shadow transition-colors">
                    Go Back
                </button>
            </div>
        );
    }

    const handleUploadTrainging = () => {
        setUploadTraining(true);
    };
    return (
        <div className="min-h-screen bg-black transition-colors">
            {/* Yellow Header Bar */}
            <div className="px-4 py-3 bg-[#E8B800]">
                <div className="container flex items-center justify-between mx-auto">
                    <div className="flex items-center">
                        <button
                            style={{ cursor: 'pointer' }}
                            onClick={() => navigate(-1)}
                            className="mr-3 text-black transition-colors hover:text-[#333333]"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <h1 className="text-lg text-black">
                            Player Profile: <span className="font-extrabold">{player?.Player || playerData?.Player}</span>
                        </h1>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                playerId ? navigate(`/integrations?player_id=${playerId}&is_video_analytics=${true}`) : navigate(`/integrations?is_video_analytics=${true}`);
                            }}
                            className="px-4 py-2 text-sm font-medium text-[#F5F5F5] transition-colors bg-[#111111] rounded-lg hover:bg-[rgba(255,255,255,0.04)]"
                        >
                            Video Analytics
                        </button>
                    </div>
                </div>
            </div>

            <div className="container px-4 py-6 mx-auto">
                {/* Player Profile Card */}
                <div className="bg-[#0A0A0A] border-[#1A1A1A] rounded-[14px] shadow-sm border overflow-hidden mb-8">
                    <div className="p-6">
                        <div className="flex flex-col gap-6 md:flex-row">
                            {/* Player Image */}
                            <div className="flex-shrink-0">
                                <img
                                    src={player?.imageUrl || "/SLAM_logo.png"}
                                    alt={player?.Player}
                                    className="object-cover w-24 h-24 bg-[#111111] rounded-lg md:w-32 md:h-32"
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = "/SLAM_logo.png";
                                        e.target.classList.add("p-4");
                                        e.target.style.objectFit = "contain";
                                    }}
                                />
                            </div>

                            {/* Player Info */}
                            <div className="flex-1">
                                <div className="flex flex-col md:flex-row md:justify-between">
                                    <div className="flex-1">
                                        <h2 className="text-2xl md:text-3xl font-bold text-[#F5F5F5] mb-3">
                                            {player?.Player}
                                        </h2>

                                        <div className="space-y-2 text-sm">
                                            <div>
                                                <span className="text-[#999999]">Position: </span>
                                                <span className="font-medium text-[#F5F5F5]">
                                                    {playerData?.Position || 'N/A'}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="text-[#999999]">Team: </span>
                                                <span className="font-medium text-[#F5F5F5]">
                                                    {player?.team} {player?.teamAbbrev ? `(${player?.teamAbbrev})` : playerData?.TeamAbbrev}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="text-[#999999]">Class: </span>
                                                <span className="font-medium text-[#F5F5F5]">
                                                    {player?.class || "N/A"}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="text-[#999999]">Hometown: </span>
                                                <span className="font-medium text-[#F5F5F5]">
                                                    {player?.homeTown || "N/A"}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* SLAM Score and Details */}
                                    <div className="mt-4 md:mt-0 md:ml-8 md:w-64">
                                        <div className="text-right">
                                            <div className="text-sm text-[#999999] mb-1">
                                                SLAM Score
                                            </div>
                                            <div className="flex items-center justify-end gap-2 mb-3">
                                                <span className="text-3xl font-bold text-[#F5F5F5]">
                                                    {/* {player?.slamScore || 'N/A'} */}
                                                </span>
                                                <span className="text-lg text-[#999999]">
                                                    N/A /100
                                                </span>
                                            </div>

                                            {/* Progress Bar */}
                                            <div className="w-full bg-[#1A1A1A] rounded-full h-2 mb-4">
                                                <div
                                                    className="h-2 transition-all duration-300 bg-[#E8B800] rounded-full"
                                                    style={{ width: `${player?.slamScore || 0}%` }}
                                                ></div>
                                            </div>

                                            {/* Height/Weight and Location */}
                                            <div className="grid grid-cols-2 gap-4 text-sm">
                                                <div>
                                                    <div className="text-[#999999] mb-1">
                                                        Height/Weight
                                                    </div>
                                                    <div className="font-medium text-[#F5F5F5]">
                                                        {player?.height || playerData?.Height || "N/A"} / {player?.weight || playerData?.Weight || "N/A"}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="text-[#999999] mb-1">
                                                        Location
                                                    </div>
                                                    <div className="font-medium text-[#F5F5F5]">
                                                        N/A
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content Tabs */}
                {player ? (
                    <Tabs
                        value={activeMainTab}
                        onValueChange={(value) => setActiveMainTab(value)}
                        className="mb-6"
                    >
                        <TabsList className="w-full justify-start gap-0 h-auto p-0 rounded-none border-b bg-transparent border-[#1A1A1A]">
                            <TabsTrigger
                                value="playerStats"
                                className="relative px-6 py-4 rounded-none border-none data-[state=active]:shadow-none data-[state=active]:bg-transparent text-[#999999] hover:bg-[rgba(255,255,255,0.04)] data-[state=active]:text-[#E8B800] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[3px] after:bg-[#E8B800] after:rounded-t after:opacity-0 data-[state=active]:after:opacity-100"
                            >
                                <span className="flex items-center font-medium">Overview</span>
                            </TabsTrigger>
                            <TabsTrigger
                                value="offensive"
                                className="relative px-6 py-4 rounded-none border-none data-[state=active]:shadow-none data-[state=active]:bg-transparent text-[#999999] hover:bg-[rgba(255,255,255,0.04)] data-[state=active]:text-[#E8B800] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[3px] after:bg-[#E8B800] after:rounded-t after:opacity-0 data-[state=active]:after:opacity-100"
                            >
                                <span className="flex items-center font-medium">Offensive</span>
                            </TabsTrigger>
                            <TabsTrigger
                                value="defensive"
                                className="relative px-6 py-4 rounded-none border-none data-[state=active]:shadow-none data-[state=active]:bg-transparent text-[#999999] hover:bg-[rgba(255,255,255,0.04)] data-[state=active]:text-[#E8B800] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[3px] after:bg-[#E8B800] after:rounded-t after:opacity-0 data-[state=active]:after:opacity-100"
                            >
                                <span className="flex items-center font-medium">Defensive</span>
                            </TabsTrigger>
                            <TabsTrigger
                                value="athletic"
                                className="relative px-6 py-4 rounded-none border-none data-[state=active]:shadow-none data-[state=active]:bg-transparent text-[#999999] hover:bg-[rgba(255,255,255,0.04)] data-[state=active]:text-[#E8B800] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[3px] after:bg-[#E8B800] after:rounded-t after:opacity-0 data-[state=active]:after:opacity-100"
                            >
                                <span className="flex items-center font-medium">Athletic</span>
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="playerStats" className="mt-6">
                            <div className="space-y-8">
                                <CareerStatsComponent player={player} seasonStats={seasonStats} sportType={sportType} />
                            </div>
                        </TabsContent>

                        <TabsContent value="offensive" className="mt-6">
                            <div>
                                <OffensiveTab stats={player} />
                            </div>
                        </TabsContent>

                        <TabsContent value="defensive" className="mt-6">
                            <div>
                                <DefensiveTab stats={player} />
                            </div>
                        </TabsContent>

                        <TabsContent value="athletic" className="mt-6">
                            <div>
                                <AthleticDashboard player={player} playerData={playerData} />
                            </div>
                        </TabsContent>
                    </Tabs>
                ) :
                    <>
                        <div className="p-4 rounded-lg border border-[#1A1A1A] border-l-4 border-l-[#E8B800] bg-[#0A0A0A]">
                            <p className="text-sm text-[#999999]">
                                <span className="font-medium">Note:</span> This player is a{" "}
                                <span className="font-medium text-[#E8B800]">Freshman</span>, {" "}
                                <span className="font-medium text-[#E8B800]">Data Availability is Limited!</span>
                            </p>
                        </div>
                    </>
                }
            </div>
            {uploadTraining && (
                <UploadTrainingStats uploadTraining={uploadTraining} setUploadTraining={setUploadTraining} />
            )}
        </div>
    );
};

export default TeamPlayerProfileNew;